import { Box, Typography } from "@mui/material";
import { useCallback, useRef, useState } from "react";
import { KPIThresholdConfig } from "../../../models/ShiftReportConfig";

const KPI_LABELS: Record<string, string> = {
	TPH: "TPH",
	Uptime: "Uptime",
	PushRate: "Push Rate",
	Productivity: "Productivity",
};

const BOUNDARIES: { key: keyof Omit<KPIThresholdConfig, "Name"> }[] = [
	{ key: "Orange" },
	{ key: "Yellow" },
	{ key: "GreenSoft" },
	{ key: "GreenNormal" },
	{ key: "GreenHard" },
];

const SEGMENTS = [
	{ fromKey: null, toKey: "Orange" as const, color: "#F44336" },
	{ fromKey: "Orange" as const, toKey: "Yellow" as const, color: "#FF9800" },
	{ fromKey: "Yellow" as const, toKey: "GreenSoft" as const, color: "#FFC107" },
	{
		fromKey: "GreenSoft" as const,
		toKey: "GreenNormal" as const,
		color: "#8BC34A",
	},
	{
		fromKey: "GreenNormal" as const,
		toKey: "GreenHard" as const,
		color: "#4CAF50",
	},
	{ fromKey: "GreenHard" as const, toKey: null, color: "#2E7D32" },
];

type Props = {
	thresholds: KPIThresholdConfig[];
	onChange: (name: string, zone: string, value: number) => void;
	unit?: string;
};

const ThresholdBar = ({
	threshold,
	onChange,
	unit = "%",
}: {
	threshold: KPIThresholdConfig;
	onChange: (name: string, zone: string, value: number) => void;
	unit?: string;
}) => {
	const barRef = useRef<HTMLDivElement>(null);
	const [dragging, setDragging] = useState<string | null>(null);

	const getPercentFromMouse = useCallback((clientX: number): number => {
		if (!barRef.current) return 0;
		const rect = barRef.current.getBoundingClientRect();
		const pct = ((clientX - rect.left) / rect.width) * 100;
		return Math.round(Math.max(0, Math.min(100, pct)));
	}, []);

	const getClampedValue = useCallback(
		(boundaryKey: string, rawValue: number): number => {
			const idx = BOUNDARIES.findIndex((b) => b.key === boundaryKey);
			const min =
				idx > 0
					? threshold[
							BOUNDARIES[idx - 1].key as keyof Omit<KPIThresholdConfig, "Name">
						]
					: 0;
			const max =
				idx < BOUNDARIES.length - 1
					? threshold[
							BOUNDARIES[idx + 1].key as keyof Omit<KPIThresholdConfig, "Name">
						]
					: 100;
			return Math.max(min, Math.min(max, rawValue));
		},
		[threshold],
	);

	const handleMouseDown = useCallback(
		(boundaryKey: string) => (e: React.MouseEvent) => {
			e.preventDefault();
			setDragging(boundaryKey);

			const handleMouseMove = (moveEvent: MouseEvent) => {
				const raw = getPercentFromMouse(moveEvent.clientX);
				const clamped = getClampedValue(boundaryKey, raw);
				onChange(threshold.Name, boundaryKey, clamped);
			};

			const handleMouseUp = () => {
				setDragging(null);
				document.removeEventListener("mousemove", handleMouseMove);
				document.removeEventListener("mouseup", handleMouseUp);
			};

			document.addEventListener("mousemove", handleMouseMove);
			document.addEventListener("mouseup", handleMouseUp);
		},
		[threshold.Name, onChange, getPercentFromMouse, getClampedValue],
	);

	return (
		<Box sx={{ mb: 3 }}>
			<Typography
				sx={{ fontSize: "0.8rem", fontWeight: 600, mb: 0.5, color: "#555" }}
			>
				{KPI_LABELS[threshold.Name] ?? threshold.Name}
			</Typography>
			<Box
				ref={barRef}
				sx={{
					position: "relative",
					height: 32,
					borderRadius: 1,
					overflow: "visible",
					border: "1px solid #ddd",
					display: "flex",
					userSelect: "none",
				}}
			>
				{SEGMENTS.map((seg) => {
					const from = seg.fromKey ? (threshold[seg.fromKey] as number) : 0;
					const to = seg.toKey ? (threshold[seg.toKey] as number) : 100;
					const width = Math.max(to - from, 0);
					if (width === 0) return null;
					return (
						<Box
							key={seg.color}
							sx={{
								width: `${width}%`,
								backgroundColor: seg.color,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								overflow: "hidden",
								transition: dragging ? "none" : "width 0.2s ease",
							}}
						>
							<Typography
								sx={{
									fontSize: "0.8rem",
									color: "#fff",
									fontWeight: 600,
									textShadow: "0 0 2px rgba(0,0,0,0.4)",
									whiteSpace: "nowrap",
									pointerEvents: "none",
								}}
							>
								{from}
								{unit} - {to}
								{unit}
							</Typography>
						</Box>
					);
				})}

				{BOUNDARIES.map((b) => {
					const val = threshold[b.key] as number;
					return (
						<Box
							key={b.key}
							onMouseDown={handleMouseDown(b.key)}
							sx={{
								position: "absolute",
								left: `${val}%`,
								top: -4,
								bottom: -4,
								width: 14,
								marginLeft: "-7px",
								cursor: "ew-resize",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								zIndex: dragging === b.key ? 10 : 5,
								"&:hover > div, &:active > div": {
									transform: "scale(1.3)",
								},
							}}
						>
							<Box
								sx={{
									width: 8,
									height: 38,
									borderRadius: 1,
									backgroundColor: dragging === b.key ? "#333" : "#666",
									border: "1px solid #fff",
									boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
									transition: "transform 0.1s, background-color 0.1s",
								}}
							/>
						</Box>
					);
				})}
			</Box>
			<Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.25 }}>
				<Typography sx={{ fontSize: "0.6rem", color: "#888" }}>
					0{unit}
				</Typography>
				<Typography sx={{ fontSize: "0.6rem", color: "#888" }}>
					100{unit}
				</Typography>
			</Box>
		</Box>
	);
};

export const ThresholdSliders = ({ thresholds, onChange, unit }: Props) => {
	return (
		<Box>
			{thresholds.map((t) => (
				<ThresholdBar
					key={t.Name}
					threshold={t}
					onChange={onChange}
					unit={unit}
				/>
			))}
		</Box>
	);
};
